/**
 * MarketplaceSetupPanel — tax config + tax summary + Stripe reminder for vendors.
 *
 * Props:
 *   - vendorId: id of the vendor being edited (for admin impersonation)
 *   - impersonating: bool — whether to include ?vendor_id=<id> on API calls
 */
import React, { useEffect, useState } from 'react';
import { Percent, Download, ShieldAlert, Sparkles, Loader2, Save, ExternalLink } from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useApp } from '../context/AppContext';
import { toast } from 'sonner';

const CURRENT_YEAR = new Date().getFullYear();

const MarketplaceSetupPanel = ({ vendorId, impersonating }) => {
  const { format } = useApp();
  const params = impersonating && vendorId ? { vendor_id: vendorId } : undefined;
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState(null);
  const [year, setYear] = useState(CURRENT_YEAR);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);

  const load = async () => {
    try {
      const c = await api.get('/vendor-admin/tax/config', { params }).then((r) => r.data);
      setCfg(c); setDraft({});
    } catch (e) { toast.error('Could not load tax config'); }
    try {
      const s = await api.get('/vendor-admin/stripe/status', { params }).then((r) => r.data);
      setStripeStatus(s);
    } catch (e) { /* stripe endpoint may be 503 if Connect not enabled */ }
  };

  const loadSummary = async () => {
    setLoadingSummary(true);
    try {
      const r = await api.get('/vendor-admin/tax/summary', { params: { ...(params || {}), year } }).then((res) => res.data);
      setSummary(r);
    } catch (e) { toast.error('Could not load tax summary'); }
    finally { setLoadingSummary(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vendorId]);
  useEffect(() => { loadSummary(); /* eslint-disable-next-line */ }, [year, vendorId]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch('/vendor-admin/tax/config', draft, { params }).then((res) => res.data);
      setCfg(r); setDraft({});
      toast.success('Tax settings saved.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const downloadCsv = async () => {
    const q = new URLSearchParams({ ...(params || {}), year: String(year) }).toString();
    const url = `${api.defaults.baseURL}/vendor-admin/tax/summary.csv?${q}`;
    // Use the auth token from axios interceptors — easier: fetch as blob.
    const token = localStorage.getItem('swiftdash_token');
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `tax_summary_${year}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error('Download failed'); }
  };

  const val = (k) => (draft[k] !== undefined ? draft[k] : cfg?.[k]);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const dirty = Object.keys(draft).length > 0;

  if (!cfg) return null;

  const needsStripe = !cfg.stripe_connected || (stripeStatus && !stripeStatus.onboarded);
  const needsTax = !cfg.category_tax_exempt_default && !(cfg.tax_rate > 0);

  return (
    <div className="mt-6 space-y-6">
      {(needsStripe || needsTax) && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3" data-testid="marketplace-setup-reminder">
          <ShieldAlert className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold text-amber-900">Finish setting up your payouts</div>
            <ul className="mt-1 text-sm text-amber-800 list-disc list-inside space-y-0.5">
              {needsStripe && <li>Connect your Stripe account so we can pay out your goods + tax.</li>}
              {needsTax && <li>Set your tax rate below (or confirm your category is tax-exempt).</li>}
              <li>You&apos;re responsible for filing your annual tax return with the government. Use the CSV export below.</li>
            </ul>
          </div>
        </div>
      )}

      <section className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Percent className="h-5 w-5 text-emerald-600" />
          <h2 className="font-semibold text-slate-900">Tax settings</h2>
          {cfg.category_tax_exempt_default && (
            <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-medium" data-testid="tax-exempt-category-pill">
              <Sparkles className="h-3 w-3" /> Food category — items tax-exempt by default
            </span>
          )}
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Tax is applied to each order&apos;s taxable subtotal (food + snacks are exempt by default; you can override any individual product).
          You&apos;re responsible for remitting the collected tax to your government.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs text-slate-600">Sales tax rate (%)</Label>
            <Input type="number" min={0} max={100} step="0.01"
              value={val('tax_rate') ?? 0}
              onChange={(e) => set('tax_rate', e.target.value === '' ? 0 : Number(e.target.value))}
              placeholder="e.g. 13"
              className="h-10 mt-1"
              data-testid="tax-rate-input" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Tax country (ISO code)</Label>
            <Input value={val('tax_country') ?? 'US'}
              onChange={(e) => set('tax_country', e.target.value.toUpperCase().slice(0, 2))}
              placeholder="US / CA / GB"
              className="h-10 mt-1"
              data-testid="tax-country-input" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Tax registration # (optional)</Label>
            <Input value={val('tax_number') ?? ''}
              onChange={(e) => set('tax_number', e.target.value)}
              placeholder="e.g. 12345 RT0001"
              className="h-10 mt-1"
              data-testid="tax-number-input" />
          </div>
        </div>

        {dirty ? (
          <div className="mt-4 flex justify-end">
            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-tax-config-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-1.5" />}
              Save tax settings
            </Button>
          </div>
        ) : (
          <div className="mt-4 flex justify-end">
            <Button disabled variant="outline" size="sm" className="opacity-60" data-testid="save-tax-config-btn">
              <Save className="h-4 w-4 mr-1.5" /> Saved
            </Button>
          </div>
        )}
      </section>

      <section className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="tax-summary-tile">
        <div className="flex items-center gap-2 mb-3">
          <Download className="h-5 w-5 text-emerald-600" />
          <h2 className="font-semibold text-slate-900">Annual tax report</h2>
        </div>
        <div className="flex items-center gap-2 mb-4">
          <Label className="text-xs text-slate-600">Year:</Label>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}
            className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="tax-year-select">
            {[CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <Button onClick={downloadCsv} variant="outline" size="sm" data-testid="download-tax-csv-btn">
            <Download className="h-4 w-4 mr-1.5" /> Download CSV
          </Button>
        </div>

        {loadingSummary ? (
          <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
        ) : summary ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Metric label="Delivered orders" value={summary.orders_count} />
            <Metric label="Taxable subtotal" value={format(summary.taxable_subtotal || 0)} />
            <Metric label="Tax collected" value={format(summary.tax_collected || 0)} />
            <Metric label="Vendor revenue" value={format(summary.vendor_revenue || 0)} highlight />
          </div>
        ) : (
          <div className="text-sm text-slate-500">No data for {year}.</div>
        )}
      </section>
    </div>
  );
};

const Metric = ({ label, value, highlight }) => (
  <div className={`rounded-xl border p-3 ${highlight ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200 bg-slate-50/50'}`}>
    <div className="text-xs text-slate-500">{label}</div>
    <div className={`mt-1 font-semibold ${highlight ? 'text-emerald-700' : 'text-slate-900'}`}>{value}</div>
  </div>
);

export default MarketplaceSetupPanel;
